import { useEffect, useState } from 'react'
import { createBreakpoint } from 'react-use'
import type { BadgeTheme } from '@which/seatbelt'
import {
  Badge,
  ButtonLink,
  CardContainer,
  Link,
  ProductScoreGauge,
  TypographyV2 as Typography,
} from '@which/seatbelt'
import { dynamicGa4DataLayerPush } from '@which/shared'

import classnames from 'classnames'
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser'
import parse, { domToReact, Element } from 'html-react-parser'
import DOMPurify from 'isomorphic-dompurify'

import { Badge as RankingBadge } from '../../../../shared/components/Badge/Badge'
import styles from './ManualReviewCard.module.scss'

export const ManualReviewCard = ({
  cardBadge,
  heading,
  description,
  callToAction,
  linkToPartner,
  scoreGauge,
  policiesData,
  rankingData,
}: Props) => {
  const badgeCleaned = cardBadge?.toLowerCase().replace(/[^a-z0-9\s]/g, '')
  const recommendedProvider = 'recommended provider'

  const useBreakpoint = createBreakpoint({ mobile: 320, tablet: 768, desktop: 1024 })
  const breakpoint = useBreakpoint()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    setIsMobile(breakpoint === 'mobile')
  }, [breakpoint])

  const trackingClickHandler = ({ event, item_url, item_text }) => {
    dynamicGa4DataLayerPush({
      event,
      item_text,
      item_parent_text: heading,
      item_group: 'manual review card',
      ...(item_url && { item_url }),
    })
  }

  const parseDescriptionText = () => {
    if (!description) {
      return null
    }

    const sanitizedHTML = DOMPurify.sanitize(description)
    const options: HTMLReactParserOptions = {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element && domNode.name === 'a') {
          const { href } = domNode.attribs
          return (
            <Link href={href} data-testid="standard-link-primary">
              {domToReact(domNode.children as DOMNode[])}
            </Link>
          )
        }
      },
    }

    return (
      <Typography textStyle="sb-text-body-default-regular">
        {parse(sanitizedHTML, options)}
      </Typography>
    )
  }

  return (
    <div className={styles.card} aria-label={`review card for ${heading}`}>
      <CardContainer>
        <div className={classnames(rankingData && styles.cardExtended)}>
          {cardBadge && (
            <div>
              <Badge
                text={badgeCleaned === recommendedProvider ? cardBadge : ''}
                theme={
                  badgeCleaned !== recommendedProvider ? (badgeCleaned as BadgeTheme) : 'default'
                }
                className={styles.cardBadge}
              />
            </div>
          )}

          <div className={classnames(styles.cardHead, !scoreGauge && styles.cardNoScoreGauge)}>
            <div className={styles.cardHeadContent}>
              {heading && (
                <Typography
                  tag="h3"
                  textStyle="sb-text-heading-small"
                  className={styles.cardHeading}
                >
                  {heading}
                </Typography>
              )}

              {description && parseDescriptionText()}
            </div>

            {scoreGauge && (
              <div className={styles.cardScoreGauge}>
                <ProductScoreGauge
                  label={scoreGauge.header ?? 'Overall score'}
                  testScoreGauge={{
                    overallScore: scoreGauge.score ? parseInt(scoreGauge.score, 10) : 0,
                    textVariation: undefined,
                  }}
                  {...(scoreGauge.tooltip && {
                    tooltip: {
                      ariaLabel: scoreGauge.tooltip,
                      contents: scoreGauge.tooltip,
                      title: scoreGauge.header ?? '',
                      openHandler: () =>
                        trackingClickHandler({
                          event: 'clickTooltip - ManualReviewCard',
                          item_text: scoreGauge.tooltip,
                          item_url: undefined,
                        }),
                    },
                  })}
                  variant={isMobile ? 'small-landscape' : 'default'}
                />
              </div>
            )}
          </div>

          {(callToAction || linkToPartner) && (
            <div className={styles.cta}>
              {callToAction && (
                <ButtonLink
                  className={styles.ctaButton}
                  appearance="secondary"
                  data-testid="cta-button"
                  href={callToAction.href}
                  aria-label={callToAction.ariaLabel}
                  onClick={() =>
                    trackingClickHandler({
                      event: 'clickButton - ManualReviewCard',
                      item_text: callToAction.text,
                      item_url: callToAction.href,
                    })
                  }
                >
                  {callToAction.text}
                </ButtonLink>
              )}

              {linkToPartner && (
                <Link
                  href={linkToPartner.href}
                  className={styles.linkToPartner}
                  includeExternalLinkIcon
                  onClick={() =>
                    trackingClickHandler({
                      event: 'clickLink - ManualReviewCard',
                      item_text: linkToPartner.text,
                      item_url: linkToPartner.href,
                    })
                  }
                >
                  {linkToPartner.text}
                </Link>
              )}
            </div>
          )}

          {policiesData && policiesData.length > 0 && (
            <div className={styles.policies}>
              <Typography textStyle="sb-text-heading-overline">Policies</Typography>
              <ul className={styles.policiesList}>
                {policiesData.map((policy, index) => (
                  <li key={index}>
                    {policy.badge && (
                      <div className={styles.policiesListBadge}>
                        <Badge
                          theme={
                            policy.badge.toLowerCase().replace(/[^a-z0-9\s]/g, '') as BadgeTheme
                          }
                        />
                      </div>
                    )}
                    <Link href={policy.href} textStyle="sb-text-interface-body-small-strong">
                      {policy.name}
                    </Link>
                    {policy.score !== undefined && (
                      <Typography
                        tag="span"
                        textStyle="sb-text-interface-body-small-regular"
                        className={styles.policiesListScore}
                      >
                        {policy.score}
                      </Typography>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {rankingData && (
            <div className={styles.ranking}>
              <div className={styles.rankingWrapper}>
                <RankingBadge
                  colour="white"
                  backgroundColour="dark"
                  className={styles.rankingBadge}
                >
                  {rankingData.number}
                </RankingBadge>
                <Typography textStyle="sb-text-interface-body-small-regular">
                  {rankingData.text}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </CardContainer>
    </div>
  )
}

type Props = {
  cardBadge?: string
  heading?: string
  description?: string
  callToAction?: {
    text?: string
    href?: string
    ariaLabel?: string
  }
  linkToPartner?: {
    text?: string
    href?: string
  }
  scoreGauge?: {
    header?: string
    score?: string
    tooltip?: string
  }
  policiesData?: {
    badge?: string
    name?: string
    href?: string
    score?: string
  }[]
  rankingData?: {
    number?: string
    text?: string
  }
}
